import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { Carousel, Spinner } from 'react-bootstrap';
import { Box, Typography, Chip, Grid, Button, Breadcrumbs, Link, Drawer, IconButton, Divider, TextField } from '@mui/material';
import { Close } from "@mui/icons-material";
import styled from 'styled-components';
import BASE_API_URL from '../apis/index';

function ProductDetails() {
    const [product, setProduct] = useState(null);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [quantity, setQuantity] = useState(1);

    const useQuery = () => { return new URLSearchParams(useLocation().search); };

    const prodId = useQuery().get("prodId");

    useEffect(() => {
        async function fetchProductDetails() {
            try {
                const authToken = localStorage.getItem('authToken')
                const res = await axios.get(`${BASE_API_URL}/api/buyer/product/${prodId}`, {
                    headers: { Authorization: `Bearer ${authToken}` }
                });

                // console.log(res.data);

                setProduct(res.data);
                setQuantity(() => parseInt(res.data.moq.split(" ")[0]))
            } catch (err) {
                console.error('Failed to fetch product data', err);
            }
        }

        fetchProductDetails();
    }, [prodId]);

    if (!product) {
        return (
            <Box display="flex" justifyContent="center" mt={5}>
                <Spinner animation="border" />
            </Box>
        );
    }

    // const tiers = JSON.parse(product.tiered_pricing || "[]");
    let tiers = [];
    try {
        tiers = (product.tiered_pricing && product.tiered_pricing !== "null")
            ? JSON.parse(product.tiered_pricing)
            : [];
    } catch {
        tiers = [];
    }

    // Helper to get price by qty
    const getPriceForQty = (qty) => {
        if (!Array.isArray(tiers) || tiers.length === 0) return null;
        for (let t of tiers) {
            if (t.max === "∞" || (qty >= t.min && qty <= t.max)) {
                return parseFloat(t.p);
            }
        }
        return 0;
    };
    const currentPrice = getPriceForQty(quantity);

    const imageBaseUrl = `${BASE_API_URL}/uploads/user_prod_docs/`;
    const images = JSON.parse(product.product_images || '[]');
    const documents = JSON.parse(product.supporting_documents || '[]');
    const documentNames = JSON.parse(product.document_names || '[]');
    const features = JSON.parse(product.features || '[]');
    const grades = JSON.parse(product.grades || '[]');

    const maxPrice = (product.max_price && product.max_price !== "null" && product.max_price !== "")
        ? Number(product.max_price)
        : null;

    const minPrice = (product.min_price && product.min_price !== "null" && product.min_price !== "")
        ? Number(product.min_price)
        : null;


    return (
        <Wrapper className='container p-4 bg-white rounded-4'>
            {/* BreadCrumbs */}
            <Breadcrumbs aria-label="breadcrumb" className='my-2 pageBreadCrumbs bg-light py-2 px-4 rounded'>
                <Link underline="hover" color="inherit" href="/"> Home </Link>
                <Link color="inherit" href={`/company-details?compId=${product.company_id}`}> {product.company_name} </Link>
                <Typography sx={{ color: 'text.primary' }}> {product.product_name} </Typography>
            </Breadcrumbs>

            <Typography variant="h5" className='mt-4 ps-4' gutterBottom>{product.product_name}</Typography>

            <Grid container spacing={2} mt={2}>
                <Grid size={{ xs: 12, md: 7 }}>
                    <div className='bg_light_secondary px-3 py-1 rounded-3 compDetilsBar'>
                        <img src={`${BASE_API_URL}/uploads/user_comp_docs/${product.company_logo}`} alt="compLogo" className='compLogo me-4' />
                        <a href={`/company-details?compId=${product.company_id}`} className='text-dark'> {product.company_name} </a>
                        <span className='ms-4 small'> {product.country_of_origin} </span>
                    </div>

                    {/* Carousel */}
                    <div className='productImagesBox'>
                        {images.length > 0 ? (
                            <Carousel id='productImagesCarousel'>
                                {images.map((img, idx) => (
                                    <Carousel.Item key={idx}>
                                        <img
                                            className="d-block w-100"
                                            src={`${imageBaseUrl}${img}`}
                                            alt={`Slide ${idx + 1}`}
                                        />
                                    </Carousel.Item>
                                ))}
                            </Carousel>
                        ) : (
                            <Typography>No images available</Typography>
                        )}
                    </div>

                    <h5 className="sub_heading fw-medium">Description:</h5>
                    <p> {product.product_description} </p>
                    {product.tech_details && (<p> <strong>Technical Details:</strong> {product.tech_details} </p>)}

                    <h5 className="sub_heading fw-medium mt-5">Key Attributes:</h5>
                    <div className="border d-flex flex-wrap">
                        {product.category && (
                            <div className="w-50">
                                <div className='d-flex'>
                                    <div className='w-50 bg-light p-3'>Category</div>
                                    <div className='w-50 p-3'>{product.category}</div>
                                </div>
                            </div>
                        )}
                        {product.product_type && (
                            <div className="w-50">
                                <div className='d-flex'>
                                    <div className='w-50 bg-light p-3'>Product Type</div>
                                    <div className='w-50 p-3'>{product.product_type}</div>
                                </div>
                            </div>
                        )}
                        {product.concentration && (
                            <div className="w-50 border-top">
                                <div className='d-flex'>
                                    <div className='w-50 bg-light p-3'>Concentration</div>
                                    <div className='w-50 p-3'>{product.concentration}</div>
                                </div>
                            </div>
                        )}
                        {product.formats && (
                            <div className="w-50 border-top">
                                <div className='d-flex'>
                                    <div className='w-50 bg-light p-3'>Formats Available</div>
                                    <div className='w-50 p-3'>{product.formats}</div>
                                </div>
                            </div>
                        )}
                        {features.length > 0 && (
                            <div className="w-100 border-top">
                                <div className='d-flex'>
                                    <div className='w-25 bg-light p-3'>Features</div>
                                    <div className='w-75 p-3'>
                                        {features.map((feature, i) => (
                                            <Chip className='rounded-1' key={i} label={feature} sx={{ mr: 1, mb: 1 }} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                        {grades.length > 0 && (
                            <div className="w-100 border-top">
                                <div className='d-flex'>
                                    <div className='w-25 bg-light p-3'>Grades</div>
                                    <div className='w-75 p-3'>
                                        {grades.map((feature, i) => (
                                            <Chip className='rounded-1' key={i} label={feature} sx={{ mr: 1, mb: 1 }} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                        {product.coas && (
                            <div className="w-100 border-top">
                                <div className='d-flex'>
                                    <div className='w-25 bg-light p-3'>CoAs</div>
                                    <div className='w-75 p-3'>
                                        <a
                                            href={`${BASE_API_URL}/uploads/user_prod_docs/${product.coas}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className='txt_color_secondary txt_color_secondary_hvr'
                                        >
                                            View CoAs file
                                        </a>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <h5 className="sub_heading fw-medium mt-5">Supporting Documents:</h5>
                    {documents.length > 0 && (
                        <Grid container spacing={2}>
                            {documents.map((doc, i) => (
                                <Grid item xs={12} sm={6} md={4} key={i}>
                                    <Button
                                        variant="outlined"
                                        color="warning"
                                        href={`${imageBaseUrl}${doc}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-100"
                                    >
                                        {documentNames[i] || `Document ${i + 1}`}
                                    </Button>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Grid>

                <Grid size={{ xs: 12, md: 5 }} className="ps-md-5">
                    <div className='border rounded-5 product_right_box p-4'>
                        <div className='w-100'>
                            {product.currency && (
                                <>
                                    <div className='d-flex align-items-center'>
                                        <h6 className="mb-0">Price Tiers</h6>
                                        {product.moq && (<span className='small mb-0 px-3 py-2 bg-light ms-3 rounded-4'> <strong>MOQ:</strong> {product.moq} </span>)}
                                    </div>

                                    <div className="d-flex gap-4 flex-wrap border-bottom py-3">
                                        {(() => {
                                            // const tiers = JSON.parse(product.tiered_pricing || '[]');
                                            return tiers.length > 0 ? (
                                                tiers.map((tier, i) => (
                                                    <Box
                                                        key={i}
                                                        display="w-33"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                    // className={`${i !== tiers.length - 1 ? 'border-bottom' : ''}`}
                                                    >
                                                        <p className="mb-2">
                                                            {tier.max === '∞'
                                                                ? `${tier.min}+ units`
                                                                : `${tier.min} - ${tier.max} units`}
                                                        </p>
                                                        <h4 className="fw-bold text-dark">
                                                            {tier.p}
                                                            <span className="x-small fw-light"> {product.currency} </span>
                                                        </h4>
                                                    </Box>
                                                ))
                                            ) : maxPrice && minPrice ? (
                                                <div>
                                                    <Typography variant="body2" py={2}> No tiered pricing available </Typography>
                                                    <div className='d-flex gap-4'>
                                                        {product.min_price && product.max_price ? (
                                                            <>
                                                                <div>
                                                                    <p className="mb-2"> Max. Price </p>
                                                                    <h4 className="fw-bold text-dark">
                                                                        {product.max_price}
                                                                        <span className="x-small fw-light"> {product.currency} </span>
                                                                    </h4>
                                                                </div>
                                                                <div>
                                                                    <p className="mb-2"> Min. Price </p>
                                                                    <h4 className="fw-bold text-dark">
                                                                        {product.min_price}
                                                                        <span className="x-small fw-light"> {product.currency} </span>
                                                                    </h4>
                                                                </div>
                                                            </>
                                                        ) : (<div>
                                                            <p className="mb-2"> Price </p>
                                                            <h4 className="fw-bold text-dark">
                                                                {product.pricing}
                                                                <span className="x-small fw-light"> {product.currency} </span>
                                                            </h4>
                                                        </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div>
                                                        <p className="mb-2"> Price </p>
                                                        <h4 className="fw-bold text-dark">
                                                            {product.pricing}
                                                            <span className="x-small fw-light"> {product.currency} </span>
                                                        </h4>
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </>
                            )}
                        </div>

                        <div className='w-100'>
                            <Button
                                variant="contained"
                                color="warning"
                                fullWidth
                                sx={{ borderRadius: '30px', py: 1.5, fontWeight: 600 }}
                                className='shadow-none'
                                onClick={() => setOpenDrawer(true)}
                            >
                                Enquire Now
                            </Button>
                        </div>
                    </div>
                </Grid>

            </Grid>

            <SideDrawer
                openDrawer={openDrawer}
                setOpenDrawer={setOpenDrawer}
                tiers={tiers}
                product={product}
                currentPrice={currentPrice}
                quantity={quantity}
                setQuantity={setQuantity}
                image={images[0]}
                maxPrice={maxPrice}
                minPrice={minPrice}
            />
        </Wrapper>
    );
}

export default ProductDetails;

const SideDrawer = ({
    openDrawer,
    setOpenDrawer,
    tiers,
    product,
    currentPrice,
    quantity,
    setQuantity,
    image,
    maxPrice,
    minPrice
}) => {
    return (
        <Drawer
            anchor="right"
            open={openDrawer}
            onClose={() => setOpenDrawer(false)}
            PaperProps={{ sx: { width: { xs: "100%", sm: 650 }, p: 4, borderTopLeftRadius: 20, borderBottomLeftRadius: 20 }, }}
        >
            <DrawerWrapper>
                <div className='DrawerSec'>
                    <div className='mb-auto'>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6" fontWeight={600}>
                                Select Variations and Quantity
                            </Typography>
                            <IconButton onClick={() => setOpenDrawer(false)}>
                                <Close />
                            </IconButton>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="subtitle1" fontWeight={600} mb={1}> Price Tiers </Typography>
                        <Box mb={2}>
                            <div className="d-flex gap-5 flex-wrap py-3">
                                {
                                    tiers.length > 0 ? (
                                        tiers.map((tier, i) => (
                                            <Box
                                                key={i}
                                                display="w-33"
                                                justifyContent="space-between"
                                                alignItems="center"
                                            >
                                                <p className="mb-2">
                                                    {tier.max === '∞'
                                                        ? `${tier.min}+ units`
                                                        : `${tier.min} - ${tier.max} units`}
                                                </p>
                                                <h4 className="fw-bold text-dark">
                                                    {tier.p}
                                                    <span className="x-small fw-light"> {product.currency} </span>
                                                </h4>
                                            </Box>
                                        ))
                                    ) : maxPrice && minPrice ? (
                                        <div>
                                            <Typography variant="body2" py={2}> No tiered pricing available </Typography>
                                            <div className='d-flex gap-4'>
                                                {product.min_price && product.max_price ? (
                                                    <>
                                                        <div>
                                                            <p className="mb-2"> Max. Price </p>
                                                            <h4 className="fw-bold text-dark">
                                                                {product.max_price}
                                                                <span className="x-small fw-light"> {product.currency} </span>
                                                            </h4>
                                                        </div>
                                                        <div>
                                                            <p className="mb-2"> Min. Price </p>
                                                            <h4 className="fw-bold text-dark">
                                                                {product.min_price}
                                                                <span className="x-small fw-light"> {product.currency} </span>
                                                            </h4>
                                                        </div>
                                                    </>
                                                ) : (<div>
                                                    <p className="mb-2"> Price </p>
                                                    <h4 className="fw-bold text-dark">
                                                        {product.pricing}
                                                        <span className="x-small fw-light"> {product.currency} </span>
                                                    </h4>
                                                </div>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <Typography variant="body2">Price not available</Typography>
                                    )
                                }
                            </div>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle1" fontWeight={600} mb={1}> Quantity </Typography>

                        <div className='d-flex justify-content-between'>
                            <div>
                                <img src={`${BASE_API_URL}/uploads/user_prod_docs/${image}`} alt="product" className='prod_img me-2' />
                                <span>{product.product_name}</span>
                            </div>
                            <div>
                                <Box className="quantitySetter">
                                    <button className='btn rounded-0' onClick={() => setQuantity((q) => Math.max(1, q - 1))}>-</button>
                                    <TextField
                                        type="number"
                                        size="small"
                                        className='qntyField rounded-0'
                                        value={quantity}
                                        onChange={(e) => setQuantity(Number(e.target.value))}
                                        inputProps={{ min: quantity, style: { textAlign: "center", width: 50 } }}
                                    />
                                    <button className='btn rounded-0' onClick={() => setQuantity((q) => q + 1)}>+</button>
                                </Box>

                            </div>
                        </div>

                    </div>

                    <div className='sideBarBottom_sec'>
                        <Divider sx={{ my: 2 }} />

                        <div className='d-flex justify-content-between align-items-center fw-bold'>
                            <div> <span className='fw-medium'>Subtotal:</span></div>
                            <div className='d-flex'>
                                {
                                    currentPrice ? (
                                        <>
                                            <div className='text-dark'>{product.currency} {(currentPrice * quantity).toFixed(2)}</div>
                                            <div className='text-dark'>{`(${product.currency} ${currentPrice.toFixed(2)}/Piece)`}</div>
                                        </>
                                    ) : maxPrice ? (
                                        <>
                                            <div className='text-dark'>{product.currency} {(product.max_price * quantity).toFixed(2)}</div>
                                        </>
                                    ) : (
                                        <div>Price not available</div>
                                    )
                                }
                            </div>
                        </div>

                        <Button
                            variant="contained"
                            color="warning"
                            className='w-100 shadow-none'
                            sx={{ mt: 3, py: 1.5, fontWeight: 600 }}
                        >
                            Send Enquiry
                        </Button>
                    </div>
                </div>
            </DrawerWrapper>
        </Drawer>
    )
}

const Wrapper = styled.div`
min-height: 95vh;
margin-bottom: 50px;
.productImagesBox{
    margin-bottom: 100px;
    margin-top: 10px; 
}
#productImagesCarousel{
  .carousel-inner{
    border-radius: 10px;
    border: 1px solid #f1f1f1;
    .carousel-item{
      width: 100%;
      height: 400px;
      border: 1px solid #f1f1f1;
      img{
        height: 400px;
        object-fit: cover;
        object-position: 50% 50%;
        border-radius: 0;
      }
    }
  }
  .carousel-indicators{
    margin: 0;
    bottom: -50px;
    button{
      background-color: var(--primary-color);
    }
  }
}

.compLogo{
    width: 40px;
    height: 50px;
    object-fit: contain;
}
.product_right_box {
  min-height: 35vh;
  position: sticky;
  top: 50px;
  background: white;
  z-index: 20;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
}
`;

const DrawerWrapper = styled.div`
width: 100%;
height: 100%;
position: relative;
.MuiDrawer-paper {
  transition: all 0.3s ease;
}
.sideBarBottom_sec{
    position: absolute;
    bottom: 0;
    width: 100%;
}
.prod_img{
    width: 60px;
    height: 40px;
    object-fit: cover;
    border: 4px solid #f1f1f1;
    border-radius: 8px;
}
.quantitySetter{
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #f3f3f3;
    border-radius: 5px;
    overflow: hidden;

    button{
        padding: 6px 10px;
        background: #f3f3f3;
    }
    .qntyField input{
        border-radius: none;
        height: 30px;
        padding: 5px 10px;
    }
}
input::-webkit-outer-spin-button, input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
`;